<template>
  <div class="p-6 dark:text-white" v-if="config">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="$router.push({ name: 'SSHCollectorConfigs' })"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
          </svg>
          Back
        </button>
        SSH Runner Config: {{ config.name }}
      </h1>
    </div>

    <!-- Status Badge -->
    <div class="mb-4">
      <span :class="[
        'px-3 py-1 text-sm rounded-full',
        config.pending ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
      ]">
        {{ config.pending ? 'Pending Approval' : 'Approved' }}
      </span>
      <span v-if="config.marked_for_deletion" class="ml-2 px-3 py-1 text-sm rounded-full bg-red-100 text-red-800">
        Marked for Deletion
      </span>
    </div>

    <!-- Config Info Card -->
    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <h2 class="text-lg font-semibold mb-4">Configuration Details</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Linked Entity</label>
          <p class="font-medium">
            <span :class="[
              'px-2 py-1 text-xs rounded-full mr-2',
              config.linked_entity_type === 'probe' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
            ]">
              {{ config.linked_entity_type }}
            </span>
            {{ config.linked_entity_name }}
          </p>
        </div>
        <div v-if="config.influxdb_url">
          <label class="text-sm text-gray-500 dark:text-gray-400">InfluxDB URL</label>
          <p class="font-medium">{{ config.influxdb_url }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">HA Cluster</label>
          <p class="font-medium">{{ config.ha_cluster ? 'Yes' : 'No' }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Created</label>
          <p class="font-medium">{{ formatDateTime(config.created_date) }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Last Modified</label>
          <p class="font-medium">{{ formatDateTime(config.last_modified) }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Organization</label>
          <p class="font-medium">{{ config.org_name || 'N/A' }}</p>
        </div>
      </div>
    </div>

    <!-- Tabs (mirrors ProbeDetails tab styling) -->
    <div class="glass-card mb-2">
      <nav class="flex px-2 py-2">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="activeTab = tab.key"
          :class="[
            'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium transition-colors duration-150 ease-in-out',
            activeTab === tab.key
              ? 'bg-white text-blue-600 border-gray-300 -mb-px dark:bg-gray-800 dark:text-blue-400'
              : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400'
          ]"
        >
          {{ tab.label }}
          <span class="ml-2 px-2 py-0.5 text-xs rounded-full border border-gray-300/60 dark:border-gray-600/60">
            {{ getJobCount(tab.key) }}
          </span>
        </button>
      </nav>
    </div>

    <!-- Tab Content -->
    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">

      <!-- Tab Content -->
      <div class="p-6">
        <!-- Targets Tab -->
        <div v-if="activeTab === 'targets' && (canViewTarget || user?.is_staff)">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">From Probe</h3>
            <button
              v-if="canAddTarget || user?.is_staff"
              @click="$router.push({ name: 'AddSSHCollectorTarget', params: { configId: config.id }, query: { schemaName: currentSchema } })"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              Add From-Probe
            </button>
          </div>
          <JobTable
            :jobs="config.targets || []"
            :columns="targetColumns"
            :can-edit="canChangeTarget || user?.is_staff"
            :can-delete="canDeleteTarget || user?.is_staff"
            @delete="deleteJob('targets', $event)"
            @edit="editJob('targets', $event)"
          />
        </div>

        <!-- Central Servers Tab -->
        <div v-if="activeTab === 'medicross' && (canViewMedicrossSite || user?.is_staff)">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">Central Servers</h3>
            <button
              v-if="canAddMedicrossSite || user?.is_staff"
              @click="$router.push({ name: 'AddSSHCollectorMedicrossSite', params: { configId: config.id }, query: { schemaName: currentSchema } })"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              Add Central Server
            </button>
          </div>
          <JobTable
            :jobs="config.medicross_sites || []"
            :columns="medicrossColumns"
            :can-edit="canChangeMedicrossSite || user?.is_staff"
            :can-delete="canDeleteMedicrossSite || user?.is_staff"
            @delete="deleteJob('medicross', $event)"
            @edit="editJob('medicross', $event)"
          />
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingJob" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
        <p class="mb-4">Are you sure you want to delete this {{ deletingJob.type === 'targets' ? 'from-probe entry' : 'central server' }}?</p>
        <p class="font-medium text-gray-600 dark:text-gray-300 mb-6">{{ deletingJob.job.fortigate_ip || deletingJob.job.name }}</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="deletingJob = null"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            @click="confirmDeleteJob"
            class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div v-else-if="!error" class="p-6">
    <p>Loading...</p>
  </div>

  <!-- Error State -->
  <PageError v-if="error" :error="error" :errorCode="errorCode" />
</template>

<script>
import {
  getSSHCollectorConfigById,
  deleteSSHCollectorTarget,
  deleteSSHCollectorMedicross,
} from "@/services/sshCollectorService";
import PageError from "@/components/PageError.vue";
import JobTable from "@/components/JobTable.vue";
import { useStore } from "@/store";
import { formatError } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "SSHCollectorDetails",
  components: {
    PageError,
    JobTable,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      config: null,
      error: "",
      errorCode: "",
      activeTab: "targets",
      deletingJob: null,
      canApprove: false,
      canAddJobs: false,
      canAddTarget: false,
      canAddMedicrossSite: false,
      canChangeTarget: false,
      canChangeMedicrossSite: false,
      canDeleteTarget: false,
      canDeleteMedicrossSite: false,
      canViewTarget: false,
      canViewMedicrossSite: false,
      allTabs: [
        { key: "targets", label: "From Probe" },
        { key: "medicross", label: "Central Servers" },
      ],
      targetColumnsBase: [
        { key: "fortigate_ip", label: "Fortigate IP" },
        { key: "collection_interval", label: "Collection Interval" },
      ],
      medicrossColumnsBase: [
        { key: "name", label: "Name" },
        { key: "fortigate_ip", label: "Fortigate IP" },
        { key: "api_port", label: "API Port" },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
    tabs() {
      const visibility = {
        targets: this.canViewTarget,
        medicross: this.canViewMedicrossSite,
      };
      return this.allTabs.filter(t => visibility[t.key] || this.user?.is_staff);
    },
    targetColumns() {
      const base = this.targetColumnsBase;
      return this.user?.is_staff ? [{ key: "id", label: "ID" }, ...base] : base;
    },
    medicrossColumns() {
      const base = this.medicrossColumnsBase;
      return this.user?.is_staff ? [{ key: "id", label: "ID" }, ...base] : base;
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.fetchConfig();
    await this.checkPermissions();
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canApprove = await useStore.hasPermission("Can change SSH Collector Config");
        this.canAddJobs = await useStore.hasPermission("Can add SSH Collector Config");
        this.canAddTarget = await useStore.hasPermission("Can add SSH Collector Target");
        this.canAddMedicrossSite = await useStore.hasPermission("Can add SSH Collector Medicross Site");
        this.canChangeTarget = await useStore.hasPermission("Can change SSH Collector Target");
        this.canChangeMedicrossSite = await useStore.hasPermission("Can change SSH Collector Medicross Site");
        this.canDeleteTarget = await useStore.hasPermission("Can delete SSH Collector Target");
        this.canDeleteMedicrossSite = await useStore.hasPermission("Can delete SSH Collector Medicross Site");
        this.canViewTarget = await useStore.hasPermission("Can view SSH Collector Target");
        this.canViewMedicrossSite = await useStore.hasPermission("Can view SSH Collector Medicross Site");
        if (this.tabs.length && !this.tabs.some(t => t.key === this.activeTab)) {
          this.activeTab = this.tabs[0].key;
        }
      }
    },
    async fetchConfig() {
      try {
        const configId = this.$route.params.id;
        this.config = await getSSHCollectorConfigById(configId, this.currentSchema);
      } catch (error) {
        console.error("Error fetching SSH collector config:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    getJobCount(tabKey) {
      if (!this.config) return 0;
      const countMap = {
        targets: this.config.targets?.length || 0,
        medicross: this.config.medicross_sites?.length || 0,
      };
      return countMap[tabKey];
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    deleteJob(type, job) {
      this.deletingJob = { type, job };
    },
    editJob(type, job) {
      const routeMap = {
        targets: "EditSSHCollectorTarget",
        medicross: "EditSSHCollectorMedicrossSite",
      };
      this.$router.push({
        name: routeMap[type],
        params: { configId: this.config.id, jobId: job.id },
        query: { schemaName: this.currentSchema },
      });
    },
    async confirmDeleteJob() {
      if (!this.deletingJob) return;

      const { type, job } = this.deletingJob;
      const deleteMap = {
        targets: deleteSSHCollectorTarget,
        medicross: deleteSSHCollectorMedicross,
      };

      try {
        await deleteMap[type](job.id, this.currentSchema);
        await this.fetchConfig();
        this.deletingJob = null;
      } catch (error) {
        console.error("Error deleting job:", error);
        alert("Failed to delete job");
      }
    },
  },
};
</script>
